import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { fetchVerticals } from '@src/api/actions/verticals';

const VerticalDropdown = ({ value, onChange, valueType = 'id' }) => {
  const [verticals, setVerticals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    fetchVerticals()
      .then((response) => {
        if (response.data && Array.isArray(response.data)) {
          setVerticals(response.data);
        } else {
          console.error('Invalid response format');
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsError(true);
        setIsLoading(false);
        console.error('Error fetching verticals:', error);
      });
  }, []);

  const options = useMemo(() => {
    if (!verticals.length) return [];
    return verticals
      .slice()
      .sort((a, b) =>
        (a.vertical || a.name || '').localeCompare(b.vertical || b.name || '', undefined, {
          sensitivity: 'base',
        })
      );
  }, [verticals]);

  const optionValue = useCallback(
    (option) => {
      if (!option) {
        return '';
      }
      if (valueType === 'name') {
        return option.vertical || option.name || '';
      }
      return option.id;
    },
    [valueType]
  );

  const selectedOption = useMemo(
    () => options.find((option) => String(optionValue(option)) === String(value)) || null,
    [options, value, optionValue]
  );

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      loading={isLoading}
      fullWidth
      onChange={(_event, newValue) => onChange(newValue ? optionValue(newValue) : '')}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      getOptionLabel={(option) => option.vertical || option.name || `Vertical ${option.id}`}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Vertical"
          error={isError}
          helperText={isError ? 'Error loading verticals. Please try again later.' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default VerticalDropdown;
